<template>
    <div class="report-container" ref="container">
        <h5 class="title">
            {{ $t('reports.dashboards.comparisonOfSubjectAverages') }}
        </h5>
        <b-alert show class="text-center mb-0" v-if="valuesResults.length == 0 && !isLoading">
            {{ $t('values.noResultsAvailable') }}
        </b-alert>
        <apexchart v-else :series="series" :height="height" :options="chartOptions" />
    </div>
</template>
<script>
import { getValuesResults, getGeneralAveragesResults } from '@/views/reports/api'
import { decimalFormat, formatNumber } from '@/lib/utils'

const HEX_GRAY = '#6E6E6E'
const HEX_GREEN = '#468B7F'
const HEX_PURPLE = '#8C7EDF'
const HEX_RED = '#BD4545'
const HEX_YELLOW = '#F7A310'
const POLYGONS_COLORS = ['#F8F8F8', '#FFF']
const GROUPING_TYPES = ['individual', 'department', 'occupation_group', 'company']
const COLORS = [HEX_GREEN, HEX_PURPLE, HEX_RED, HEX_YELLOW]

export default {
    props: {
        filters: Object,
        height: {
            type: Number,
            default: 630,
        },
        size: {
            type: Number,
            default: 230,
        },
    },
    methods: {
        getResults() {
            const loader = this.createLoader()
            getValuesResults(this.filters)
                .then((results) => {
                    this.valuesResults = results
                })
                .catch(console.error)
            getGeneralAveragesResults(this.filters)
                .then((results) => {
                    this.results = results
                })
                .catch(console.error)
                .finally(() => {
                    this.hideLoader(loader)
                })
        },
        createLoader() {
            this.isLoading = true
            return this.$loading.show({
                width: 55,
                height: 55,
                container: this.$refs.container,
            })
        },
        hideLoader(loader) {
            this.isLoading = false
            setTimeout(() => loader.hide(), 300)
        },
        groupTypeHasNoCategories(categories) {
            return categories.length === 0
        },
    },
    mounted() {
        this.getResults()
    },
    watch: {
        filters() {
            this.getResults()
        },
    },
    data() {
        return {
            results: [],
            valuesResults: [],
            isLoading: false,
        }
    },
    computed: {
        series() {
            const data = {
                individual: {
                    name: this.$t('values.table.individual'),
                    color: HEX_GREEN,
                    data: [],
                },
                department: {
                    name: this.$t('values.table.department'),
                    color: HEX_PURPLE,
                    data: [],
                },
                company: {
                    name: this.$t('values.table.company'),
                    color: HEX_RED,
                    data: [],
                },
                occupation_group: {
                    name: this.$t('values.table.occupation_group'),
                    color: HEX_YELLOW,
                    data: [],
                },
            }

            GROUPING_TYPES.forEach((type) => {
                if (this.results[type] != undefined) {
                    this.results[type].forEach(({ categories }) => {
                        if (this.groupTypeHasNoCategories(categories)) data[type].data.push(0)
                        categories.forEach(({ values }) => {
                            values.forEach((value) => {
                                const score = value.rating_score
                                const formated = decimalFormat(score)
                                data[type].data.push(formated)
                            })
                        })
                    })
                }
            })

            return [data.individual, data.department, data.company, data.occupation_group]
        },
        categories() {
            return this.valuesResults.map(({ value }) => value.title)
        },
        colors() {
            const length = this.valuesResults.length
            return new Array(length).fill(HEX_GRAY)
        },
        chartOptions() {
            return {
                stroke: {
                    width: 2,
                    colors: COLORS,
                },
                yaxis: {
                    show: false,
                },
                fill: {
                    opacity: 0.1,
                },
                markers: {
                    size: 3,
                    colors: COLORS,
                },
                chart: {
                    type: 'radar',
                    dropShadow: {
                        enabled: true,
                        blur: 1,
                        left: 2,
                        top: 2,
                    },
                    toolbar: {
                        show: false,
                    },
                },
                plotOptions: {
                    radar: {
                        size: this.size,
                        polygons: {
                            fill: {
                                colors: POLYGONS_COLORS,
                            },
                        },
                    },
                },
                tooltip: {
                    enabled: true,
                    y: {
                        formatter: (value) => formatNumber(value),
                    },
                    x: {
                        formatter: (value) => value
                    }
                },
                xaxis: {
                    categories: this.categories,
                    labels: {
                        style: {
                            fontSize: '14px',
                            fontWeight: 700,
                            colors: this.colors,
                        },
                        formatter: (value) => {
                            const len = value.length;
                            return len > 5 ? `${value.substring(0, 5)}...` : value;                        }
                    },
                },
            }
        },
    },
}
</script>
<style lang="sass" scoped>
$gray: #6e6e6e

.report-container
    padding: 25px 30px
    border: 1px solid #ebebeb
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.24)
    .title
        font-size: 18px
        color: $gray
        font-weight: 500
        line-height: 21px
</style>
